import { activitySortTime } from "@/features/classes/admin/classEnrollmentHistory";
import type { RecentEnrollmentStatusChangeDto } from "@/features/enrollments/shared/fetchRecentEnrollmentStatusChanges";
import type { ScheduledDropEnrollmentItem } from "@/lib/dashboard/scheduled-drop-enrollments-widget";
import { PageTypeAdmin } from "@/lib/types";
import {
  enrollmentHasStatus,
  getEnrollmentStatusDisplayLabel,
  getScheduledDropDateYmd,
  getUpcomingWithdrawYmd,
  hasAdminScheduledFutureDrop,
  isActiveEnrollment,
  isBackendFutureScheduledDrop,
  isDropDateOnOrBeforeToday,
  isScheduledDropPending,
  parseEnrollmentTimestamp,
} from "@/lib/utils";
import {
  addDaysToYmd,
  formatYmdInTimeZone,
  studioTodayYmd,
} from "@/lib/utils/date/studio-clock";
import {
  EnrollmentStatus,
  type GetEnrollmentResponse,
  Origin,
} from "@/openapi/generated";

export const RECENT_ENROLLMENTS_DAYS = 7;

export type EnrollmentActivityCategory = "enrollments" | "drops" | "waitlist";

export interface RecentEnrollmentItem {
  id: string;
  enrollmentId: number;
  classId?: number;
  locationId?: number | null;
  studentName: string;
  className: string;
  status: EnrollmentStatus;
  statusLabel: string;
  activityCategory: EnrollmentActivityCategory;
  /** Admin or staff created this enrollment, or recorded this drop. */
  studioInitiated: boolean;
  href?: string;
  /** When this status change was recorded (not the effective withdraw day). */
  changedAt: Date;
  /** Studio calendar day the withdraw takes effect (YYYY-MM-DD). */
  effectiveDropDateYmd?: string | null;
}

export interface RecentEnrollmentDayGroup {
  id: "today" | "yesterday" | "earlier";
  label: string;
  items: RecentEnrollmentItem[];
}

export const RECENT_ENROLLMENT_DAY_COLORS: Record<
  RecentEnrollmentDayGroup["id"],
  string
> = {
  today: "#3B82F6",
  yesterday: "#8B5CF6",
  earlier: "#64748B",
};

export function getEnrollmentStatusLabel(status: EnrollmentStatus): string {
  if (status === EnrollmentStatus.ScheduledDrop) {
    return "Withdraw set";
  }
  return getEnrollmentStatusDisplayLabel(status) || "Enrolled";
}

export function getRecentEnrollmentStatusLabel(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt" | "dropDate">,
): string {
  if (isWithdrawSetEnrollment(enrollment)) {
    return "Withdraw set";
  }
  return getEnrollmentStatusLabel(
    enrollment.status ?? EnrollmentStatus.Pending,
  );
}

export function getEnrollmentStatusTone(
  status: EnrollmentStatus,
): "neutral" | "success" | "premium" | "danger" | "info" {
  switch (status) {
    case EnrollmentStatus.Pending:
      return "premium";
    case EnrollmentStatus.Awaiting:
      return "info";
    case EnrollmentStatus.Dropped:
    case EnrollmentStatus.ScheduledDrop:
    case EnrollmentStatus.Completed:
    case EnrollmentStatus.Cancelled:
    case EnrollmentStatus.Rejected:
      return "neutral";
    default:
      return "success";
  }
}

type EnrollmentStatusChangeFields = Pick<
  GetEnrollmentResponse,
  | "status"
  | "statusChangedAt"
  | "enrolledAt"
  | "droppedAt"
  | "updatedAt"
  | "rejectedAt"
  | "cancelledAt"
  | "completedAt"
  | "activityHistory"
  | "dropDate"
>;

function latestActivityChangeAt(
  enrollment: EnrollmentStatusChangeFields,
): Date | null {
  const history = enrollment.activityHistory ?? [];
  let latest: Date | null = null;
  for (const activity of history) {
    const at = activitySortTime(activity);
    if (!at) continue;
    if (!latest || at.getTime() > latest.getTime()) latest = at;
  }
  return latest;
}

function isWithdrawSetEnrollment(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt" | "dropDate">,
): boolean {
  if (
    isBackendFutureScheduledDrop(enrollment) ||
    isScheduledDropPending(enrollment) ||
    hasAdminScheduledFutureDrop(enrollment)
  ) {
    return true;
  }
  const upcomingYmd = getUpcomingWithdrawYmd(enrollment);
  if (!upcomingYmd) return false;
  return (
    enrollmentHasStatus(enrollment, EnrollmentStatus.Dropped) ||
    enrollmentHasStatus(enrollment, EnrollmentStatus.Enrolled)
  );
}

function latestTimestamp(...values: unknown[]): Date | null {
  let latest: Date | null = null;
  for (const value of values) {
    const parsed = parseEnrollmentTimestamp(value);
    if (!parsed) continue;
    if (!latest || parsed.getTime() > latest.getTime()) latest = parsed;
  }
  return latest;
}

/**
 * Timestamp for the Enrollment Status Changes feed.
 * Drops and withdraws use when that change happened, not the original enroll date.
 */
function isFutureEffectiveDropTimestamp(
  date: Date | null,
  dropDateYmd: string | null,
): boolean {
  if (!date || !dropDateYmd) return false;
  if (formatYmdInTimeZone(date) === dropDateYmd) return true;
  return date.getTime() > Date.now();
}

function resolveWithdrawSetChangedAt(
  enrollment: EnrollmentStatusChangeFields,
): Date | null {
  const fromActivity = latestActivityChangeAt(enrollment);
  const updatedAt = parseEnrollmentTimestamp(enrollment.updatedAt);
  const statusChangedAt = parseEnrollmentTimestamp(enrollment.statusChangedAt);
  const droppedAt = parseEnrollmentTimestamp(enrollment.droppedAt);
  const dropDateYmd = getScheduledDropDateYmd(enrollment);
  const upcomingDrop =
    dropDateYmd != null && !isDropDateOnOrBeforeToday(dropDateYmd);

  let at: Date | null;

  if (upcomingDrop) {
    const statusIsEffectiveDate = isFutureEffectiveDropTimestamp(
      statusChangedAt,
      dropDateYmd,
    );
    const droppedIsEffectiveDate = isFutureEffectiveDropTimestamp(
      droppedAt,
      dropDateYmd,
    );

    if (droppedAt && droppedAt.getTime() > Date.now()) {
      at =
        updatedAt ??
        (statusIsEffectiveDate ? null : statusChangedAt) ??
        fromActivity ??
        (droppedIsEffectiveDate ? null : droppedAt);
    } else {
      at =
        updatedAt ??
        (statusIsEffectiveDate ? null : statusChangedAt) ??
        fromActivity;
    }
  } else {
    at = updatedAt ?? statusChangedAt ?? fromActivity;
  }

  const enrolledAt = parseEnrollmentTimestamp(enrollment.enrolledAt);
  if (
    at &&
    enrolledAt &&
    at.getTime() === enrolledAt.getTime() &&
    fromActivity &&
    fromActivity.getTime() > at.getTime()
  ) {
    at = fromActivity;
  }

  if (!at && enrolledAt && !upcomingDrop) {
    at = enrolledAt;
  }

  return at;
}

export function getEnrollmentStatusChangeAt(
  enrollment: EnrollmentStatusChangeFields,
): Date | null {
  if (isWithdrawSetEnrollment(enrollment)) {
    // Future droppedAt / dropDate is the effective withdraw day, not when staff set it.
    return resolveWithdrawSetChangedAt(enrollment);
  }

  if (!isActiveEnrollment(enrollment)) {
    return latestTimestamp(
      enrollment.statusChangedAt,
      enrollment.droppedAt,
      enrollment.rejectedAt,
      enrollment.cancelledAt,
      enrollment.completedAt,
      enrollment.updatedAt,
    );
  }

  const fromFields = latestTimestamp(
    enrollment.statusChangedAt,
    enrollment.updatedAt,
    enrollment.enrolledAt,
  );
  const fromActivity = latestActivityChangeAt(enrollment);
  if (fromFields && fromActivity) {
    return fromFields.getTime() >= fromActivity.getTime()
      ? fromFields
      : fromActivity;
  }
  return fromFields ?? fromActivity;
}

export function getEnrollmentHref(
  enrollment: GetEnrollmentResponse,
): string | undefined {
  if (enrollmentHasStatus(enrollment, EnrollmentStatus.Pending)) {
    const enrollmentId = Number(enrollment.enrollmentId);
    if (Number.isFinite(enrollmentId) && enrollmentId > 0) {
      return `${PageTypeAdmin.PendingEnrollments}?enrollmentId=${enrollmentId}`;
    }
    return PageTypeAdmin.PendingEnrollments;
  }
  if (enrollment.student?.studentId != null) {
    return `${PageTypeAdmin.Students}/${enrollment.student.studentId}`;
  }
  return undefined;
}

const ENROLLMENT_EVENT_TIMESTAMP_TOLERANCE_MS = 90_000;

function enrollmentChangedOnOrAfterCutoff(
  changedAt: Date | null,
  cutoffYmd: string,
): boolean {
  if (!changedAt) return false;
  return formatYmdInTimeZone(changedAt) >= cutoffYmd;
}

function enrollmentTimestampsNearlyEqual(a: Date, b: Date): boolean {
  return (
    Math.abs(a.getTime() - b.getTime()) <=
    ENROLLMENT_EVENT_TIMESTAMP_TOLERANCE_MS
  );
}

function getRecentSignupStatusLabel(
  enrollment: Pick<GetEnrollmentResponse, "status">,
): string {
  if (enrollmentHasStatus(enrollment, EnrollmentStatus.Pending)) {
    return "Pending";
  }
  if (enrollmentHasStatus(enrollment, EnrollmentStatus.Awaiting)) {
    return "Waitlist";
  }
  return "Enrolled";
}

function shouldShowDistinctStatusChange(
  enrollment: GetEnrollmentResponse,
  enrolledAt: Date | null,
  statusAt: Date | null,
): boolean {
  if (!statusAt) return false;
  if (isWithdrawSetEnrollment(enrollment)) return true;
  if (isScheduledDropPending(enrollment)) return true;
  if (enrollmentHasStatus(enrollment, EnrollmentStatus.Dropped)) return true;
  if (
    enrollmentHasStatus(enrollment, EnrollmentStatus.Rejected) ||
    enrollmentHasStatus(enrollment, EnrollmentStatus.Cancelled) ||
    enrollmentHasStatus(enrollment, EnrollmentStatus.Completed)
  ) {
    return true;
  }
  if (!enrolledAt) return true;
  return !enrollmentTimestampsNearlyEqual(enrolledAt, statusAt);
}

function collectRecentEnrollmentItemsForRow(
  enrollment: GetEnrollmentResponse,
  index: number,
  cutoffYmd: string,
): RecentEnrollmentItem[] {
  const status = enrollment.status ?? EnrollmentStatus.Pending;
  const studentName = enrollment.student
    ? `${enrollment.student.firstName ?? ""} ${enrollment.student.lastName ?? ""}`.trim()
    : "Unknown Student";
  const enrollmentId = Number(enrollment.enrollmentId) || 0;
  const href = getEnrollmentHref(enrollment);
  const className = enrollment._class?.name || "Unknown Class";
  const classId = Number(enrollment._class?.classId) || 0;
  const items: RecentEnrollmentItem[] = [];

  const dropDateYmd = getScheduledDropDateYmd(enrollment);

  const pushItem = (
    suffix: string,
    changedAt: Date,
    statusLabel: string,
    activityCategory: EnrollmentActivityCategory,
  ) => {
    items.push({
      id: `enrollment-${enrollmentId || index}-${suffix}-${changedAt.getTime()}`,
      enrollmentId,
      classId: classId > 0 ? classId : undefined,
      studentName: studentName || "Unknown Student",
      className,
      status,
      statusLabel,
      activityCategory,
      studioInitiated: isStudioStaffEnrollment(enrollment, activityCategory),
      href,
      changedAt,
      effectiveDropDateYmd: activityCategory === "drops" ? dropDateYmd : null,
    });
  };

  const enrolledAt = parseEnrollmentTimestamp(enrollment.enrolledAt);
  if (enrollmentChangedOnOrAfterCutoff(enrolledAt, cutoffYmd) && enrolledAt) {
    const signupCategory: EnrollmentActivityCategory = enrollmentHasStatus(
      enrollment,
      EnrollmentStatus.Awaiting,
    )
      ? "waitlist"
      : "enrollments";
    pushItem(
      "signup",
      enrolledAt,
      getRecentSignupStatusLabel(enrollment),
      signupCategory,
    );
  }

  const statusAt = getEnrollmentStatusChangeAt(enrollment);
  if (
    enrollmentChangedOnOrAfterCutoff(statusAt, cutoffYmd) &&
    statusAt &&
    shouldShowDistinctStatusChange(enrollment, enrolledAt, statusAt)
  ) {
    pushItem(
      "status",
      statusAt,
      getRecentEnrollmentStatusLabel(enrollment),
      getEnrollmentActivityFilterCategory(enrollment),
    );
  }

  if (
    items.length === 0 &&
    statusAt &&
    enrollmentChangedOnOrAfterCutoff(statusAt, cutoffYmd)
  ) {
    pushItem(
      "change",
      statusAt,
      getRecentEnrollmentStatusLabel(enrollment),
      getEnrollmentActivityFilterCategory(enrollment),
    );
  }

  return items;
}

export function buildRecentEnrollmentItems(
  enrollments: readonly GetEnrollmentResponse[],
  days = RECENT_ENROLLMENTS_DAYS,
): RecentEnrollmentItem[] {
  const cutoffYmd = addDaysToYmd(studioTodayYmd(), -(days - 1));

  const items: RecentEnrollmentItem[] = [];
  enrollments.forEach((enrollment, index) => {
    items.push(
      ...collectRecentEnrollmentItemsForRow(enrollment, index, cutoffYmd),
    );
  });

  return items.sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
}

function isEnrollmentChangeWithinFeedWindow(
  changedAt: Date | null,
  days = RECENT_ENROLLMENTS_DAYS,
): boolean {
  if (!changedAt) return false;
  const cutoffYmd = addDaysToYmd(studioTodayYmd(), -(days - 1));
  return formatYmdInTimeZone(changedAt) >= cutoffYmd;
}

function enrichEnrollmentsWithScheduledDrops(
  enrollments: readonly GetEnrollmentResponse[],
  scheduledDrops: readonly ScheduledDropEnrollmentItem[],
): GetEnrollmentResponse[] {
  const byId = new Map<number, GetEnrollmentResponse>();
  for (const enrollment of enrollments) {
    const id = Number(enrollment.enrollmentId);
    if (Number.isFinite(id) && id > 0) byId.set(id, enrollment);
  }

  for (const enrollment of byId.values()) {
    if (!isWithdrawSetEnrollment(enrollment)) continue;
    const changedAt = resolveWithdrawSetChangedAt(enrollment);
    const fromActivity = latestActivityChangeAt(enrollment);
    if (
      !isEnrollmentChangeWithinFeedWindow(changedAt) &&
      fromActivity &&
      isEnrollmentChangeWithinFeedWindow(fromActivity)
    ) {
      const id = Number(enrollment.enrollmentId);
      if (!Number.isFinite(id) || id <= 0) continue;
      byId.set(id, {
        ...enrollment,
        updatedAt: fromActivity,
        statusChangedAt: fromActivity,
      });
    }
  }

  for (const row of scheduledDrops) {
    const id = row.enrollmentId;
    if (!Number.isFinite(id) || id <= 0) continue;
    const dropYmd = row.dropDateYmd;
    if (!dropYmd) continue;

    const existing = byId.get(id);
    if (existing) {
      let next = {
        ...existing,
        dropDate: dropYmd,
        droppedAt: existing.droppedAt ?? dropYmd,
        _class: {
          ...existing._class,
          classId: row.classId ?? existing._class?.classId,
          name: row.className || existing._class?.name,
        },
      } as GetEnrollmentResponse;

      if (isWithdrawSetEnrollment(next)) {
        const changedAt = resolveWithdrawSetChangedAt(next);
        const dropStillUpcoming = dropYmd >= studioTodayYmd();
        if (
          dropStillUpcoming &&
          !isEnrollmentChangeWithinFeedWindow(changedAt)
        ) {
          const now = new Date();
          next = {
            ...next,
            updatedAt: now,
            statusChangedAt: now,
          };
        }
      }

      byId.set(id, next);
      continue;
    }

    const [firstName, ...rest] = row.studentName.trim().split(/\s+/);
    const now = new Date();
    byId.set(id, {
      enrollmentId: id,
      status: EnrollmentStatus.Dropped,
      droppedAt: dropYmd,
      dropDate: dropYmd,
      updatedAt: now,
      statusChangedAt: now,
      enrolledAt: null,
      rejectedAt: null,
      cancelledAt: null,
      completedAt: null,
      activityHistory: [],
      student: {
        studentId: row.studentId ?? 0,
        firstName: firstName || "Unknown",
        lastName: rest.join(" "),
        isActive: true,
      },
      _class: {
        classId: row.classId ?? 0,
        name: row.className,
        description: null,
        isActive: true,
      },
    } as unknown as GetEnrollmentResponse);
  }

  return Array.from(byId.values());
}

/** Maps GET /api/class-enrollments/recent-status-changes rows to dashboard feed items. */
export function parseEnrollmentActivityId(feedItemId: string): number | null {
  const match = /^activity-(\d+)$/.exec(feedItemId.trim());
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function mapRecentEnrollmentStatusChangesToFeedItems(
  items: readonly RecentEnrollmentStatusChangeDto[],
): RecentEnrollmentItem[] {
  return items
    .map((item) => ({
      id: item.id,
      enrollmentId: item.enrollmentId,
      classId: item.classId,
      locationId: item.locationId ?? null,
      studentName: item.studentName || "Unknown Student",
      className: item.className || "Unknown Class",
      status: item.status,
      statusLabel: item.statusLabel,
      activityCategory: item.activityCategory,
      studioInitiated: item.studioInitiated,
      href: `${PageTypeAdmin.Students}/${item.studentId}`,
      changedAt: new Date(item.changedAt),
      effectiveDropDateYmd: item.dropDate,
    }))
    .filter((item) => !Number.isNaN(item.changedAt.getTime()))
    .sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime());
}

/** Studio dashboard feed: merge scheduled-drop widget rows so future withdraws show when they were set. */
export function buildRecentEnrollmentItemsForDashboard(
  enrollments: readonly GetEnrollmentResponse[],
  scheduledDrops: readonly ScheduledDropEnrollmentItem[] = [],
  days = RECENT_ENROLLMENTS_DAYS,
  classLocationById?: ReadonlyMap<number, number>,
): RecentEnrollmentItem[] {
  const items = buildRecentEnrollmentItems(
    enrichEnrollmentsWithScheduledDrops(enrollments, scheduledDrops),
    days,
  );
  if (!classLocationById?.size) {
    return items;
  }
  return items.map((item) => {
    const classId = item.classId;
    if (classId == null || classId <= 0) return item;
    const locationId = classLocationById.get(classId);
    return locationId != null ? { ...item, locationId } : item;
  });
}

export function filterRecentEnrollmentItemsByLocation(
  items: readonly RecentEnrollmentItem[],
  locationId: number | null,
): RecentEnrollmentItem[] {
  if (locationId == null || locationId <= 0) {
    return [...items];
  }
  return items.filter((item) => item.locationId === locationId);
}

export function buildRecentEnrollmentDayBuckets(
  items: readonly RecentEnrollmentItem[],
  now: Date = new Date(),
): RecentEnrollmentDayGroup[] {
  const todayYmd = studioTodayYmd(now);
  const yesterdayYmd = addDaysToYmd(todayYmd, -1);

  const groups: RecentEnrollmentDayGroup[] = [
    { id: "today", label: "Today", items: [] },
    { id: "yesterday", label: "Yesterday", items: [] },
    { id: "earlier", label: "Earlier this week", items: [] },
  ];

  for (const item of items) {
    const dayYmd = formatYmdInTimeZone(item.changedAt);
    if (dayYmd === todayYmd) {
      groups[0].items.push(item);
    } else if (dayYmd === yesterdayYmd) {
      groups[1].items.push(item);
    } else {
      groups[2].items.push(item);
    }
  }

  return groups;
}

export function getRecentEnrollmentDayGroupTitle(
  groupId: RecentEnrollmentDayGroup["id"],
): string {
  switch (groupId) {
    case "today":
      return "Status changes today";
    case "yesterday":
      return "Status changes yesterday";
    case "earlier":
      return "Status changes earlier this week";
    default:
      return "Enrollment status changes";
  }
}

export function getRecentEnrollmentDayGroupColor(
  groupId: RecentEnrollmentDayGroup["id"],
): string {
  return RECENT_ENROLLMENT_DAY_COLORS[groupId];
}

export type EnrollmentActivityFilter = "all" | "enrollments" | "drops";

function isStudioStaffEnrollment(
  enrollment: Pick<GetEnrollmentResponse, "origin">,
  activityCategory: EnrollmentActivityCategory,
): boolean {
  if (activityCategory !== "enrollments") return false;
  return (
    enrollment.origin === Origin.Admin || enrollment.origin === Origin.Staff
  );
}

export function getEnrollmentActivityFilterCategory(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt" | "dropDate">,
): EnrollmentActivityCategory {
  if (isWithdrawSetEnrollment(enrollment)) {
    return "drops";
  }

  const status = enrollment.status ?? EnrollmentStatus.Pending;

  if (status === EnrollmentStatus.Awaiting) {
    return "waitlist";
  }

  if (
    status === EnrollmentStatus.Dropped ||
    status === EnrollmentStatus.ScheduledDrop ||
    status === EnrollmentStatus.Completed ||
    status === EnrollmentStatus.Cancelled ||
    status === EnrollmentStatus.Rejected
  ) {
    return "drops";
  }

  return "enrollments";
}

export function filterRecentEnrollmentItemsByActivity(
  items: readonly RecentEnrollmentItem[],
  filter: EnrollmentActivityFilter,
): RecentEnrollmentItem[] {
  if (filter === "all") {
    return [...items];
  }

  return items.filter(
    (item) => item.activityCategory === filter && item.studioInitiated,
  );
}

export function applyRecentEnrollmentFeedFilters(
  items: readonly RecentEnrollmentItem[],
  activityFilter: EnrollmentActivityFilter,
  locationId: number | null,
): RecentEnrollmentItem[] {
  return filterRecentEnrollmentItemsByLocation(
    filterRecentEnrollmentItemsByActivity(items, activityFilter),
    locationId,
  );
}

export const ENROLLMENT_ACTIVITY_FILTER_LABELS: Record<
  EnrollmentActivityFilter,
  string
> = {
  all: "All",
  enrollments: "Admin & staff enrollments",
  drops: "Admin & staff drops",
};

export function getRecentEnrollmentEventCopy(
  item: Pick<RecentEnrollmentItem, "activityCategory" | "effectiveDropDateYmd">,
): {
  recordedPrefix: "Enrolled" | "Waitlisted" | "Recorded" | "Changed";
  effectiveDropDateYmd: string | null;
} {
  if (item.activityCategory === "enrollments") {
    return { recordedPrefix: "Enrolled", effectiveDropDateYmd: null };
  }
  if (item.activityCategory === "waitlist") {
    return { recordedPrefix: "Waitlisted", effectiveDropDateYmd: null };
  }
  if (item.effectiveDropDateYmd) {
    return {
      recordedPrefix: "Recorded",
      effectiveDropDateYmd: item.effectiveDropDateYmd,
    };
  }
  return { recordedPrefix: "Changed", effectiveDropDateYmd: null };
}

export function formatRecentEnrollmentTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
